import type { L10n } from "../lib/locales"

/* ---------------- Solutions ---------------- */

export type Solution = {
  slug: string
  title: L10n
  summary: L10n
  challenge: L10n
  capabilities: { title: L10n; body: L10n }[]
  services: string[]
}

export const solutions: Solution[] = [
  {
    slug: "marketing-agencies",
    title: {
      en: "Marketing & Advertising Agencies",
      ar: "وكالات التسويق والإعلان",
    },
    summary: {
      en: "Connect new business, client projects, creative tasks and reporting in one operational flow.",
      ar: "اربط الأعمال الجديدة ومشاريع العملاء والمهام الإبداعية والتقارير في مسار تشغيلي واحد.",
    },
    challenge: {
      en: "Agencies juggle many clients, retainers and deliverables at once. Briefs arrive by chat, approvals are scattered, time is hard to track against budgets, and account managers rebuild client reports every month.",
      ar: "تدير الوكالات عملاء وعقودًا شهرية ومخرجات كثيرة في وقت واحد. تصل الطلبات عبر المحادثات، وتتفرق الموافقات، ويصعب تتبع الوقت مقابل الميزانيات، ويعيد مديرو الحسابات إعداد تقارير العملاء كل شهر.",
    },
    capabilities: [
      {
        title: {
          en: "New customer follow-up",
          ar: "مسار مبيعات للأعمال الجديدة",
        },
        body: {
          en: "Track pitches and proposals from first contact to signed retainer.",
          ar: "تتبع العروض والمقترحات من أول تواصل حتى توقيع العقد.",
        },
      },
      {
        title: {
          en: "Client projects and tasks",
          ar: "مشاريع العملاء والمهام",
        },
        body: {
          en: "Projects per client with task stages, deadlines and assigned teams.",
          ar: "مشاريع لكل عميل بمراحل مهام ومواعيد وفرق مسؤولة.",
        },
      },
      {
        title: {
          en: "Creative approval workflow",
          ar: "مسار موافقات المحتوى الإبداعي",
        },
        body: {
          en: "Internal review and client approval steps recorded on each deliverable.",
          ar: "خطوات مراجعة داخلية وموافقة العميل مسجلة على كل مخرج.",
        },
      },
      {
        title: {
          en: "Retainer and timesheet tracking",
          ar: "تتبع العقود الشهرية وساعات العمل",
        },
        body: {
          en: "Compare hours spent against what each client contract includes.",
          ar: "مقارنة الساعات المنفذة بما يتضمنه عقد كل عميل.",
        },
      },
      {
        title: { en: "Automated client reporting", ar: "تقارير عملاء تلقائية" },
        body: {
          en: "Scheduled reports showing project progress and campaign results.",
          ar: "تقارير مجدولة توضح تقدم المشاريع ونتائج الحملات.",
        },
      },
    ],
    services: ["erp-crm", "automation", "ai-solutions"],
  },
  {
    slug: "real-estate",
    title: { en: "Real Estate Companies", ar: "شركات العقارات" },
    summary: {
      en: "Lead capture, sales pipelines and follow-up that keep every inquiry moving toward a viewing and a deal.",
      ar: "استقبال العملاء المحتملين ومسارات البيع والمتابعة التي تدفع كل استفسار نحو معاينة وصفقة.",
    },
    challenge: {
      en: "Leads arrive from portals, social ads, calls and walk-ins. Without one system, inquiries are lost between agents, follow-up depends on memory, and management cannot see which channels and projects perform.",
      ar: "يصل العملاء المحتملون من البوابات العقارية والإعلانات والمكالمات والزيارات. ودون نظام موحد تضيع الاستفسارات بين الوسطاء، وتعتمد المتابعة على الذاكرة، ولا ترى الإدارة أي القنوات والمشاريع أفضل أداءً.",
    },
    capabilities: [
      {
        title: {
          en: "Unified lead capture",
          ar: "استقبال موحد للعملاء المحتملين",
        },
        body: {
          en: "Website and campaign forms create leads with their source recorded.",
          ar: "نماذج الموقع والحملات تنشئ عملاء محتملين مع تسجيل المصدر.",
        },
      },
      {
        title: {
          en: "Sales pipeline by project",
          ar: "مسار مبيعات حسب المشروع",
        },
        body: {
          en: "Stages from inquiry to viewing, reservation and contract.",
          ar: "مراحل من الاستفسار إلى المعاينة ثم الحجز والعقد.",
        },
      },
      {
        title: {
          en: "Assignment and follow-up rules",
          ar: "قواعد الإسناد والمتابعة",
        },
        body: {
          en: "Distribute leads to agents and create reminders when follow-up is due.",
          ar: "توزيع العملاء على الوسطاء وإنشاء تذكيرات عند حلول موعد المتابعة.",
        },
      },
      {
        title: {
          en: "Unit inventory and reservations",
          ar: "مخزون الوحدات والحجوزات",
        },
        body: {
          en: "Track available units, holds and reservations linked to opportunities.",
          ar: "تتبع الوحدات المتاحة والحجوزات المؤقتة والنهائية المرتبطة بالفرص.",
        },
      },
      {
        title: { en: "Management reporting", ar: "تقارير الإدارة" },
        body: {
          en: "Reports on sales, lead sources and team performance.",
          ar: "عرض لمسار المبيعات والقنوات وأداء الوسطاء لقيادة المبيعات.",
        },
      },
    ],
    services: ["erp-crm", "automation", "digital-presence"],
  },
  {
    slug: "ecommerce",
    title: { en: "E-Commerce & Retail", ar: "التجارة الإلكترونية والتجزئة" },
    summary: {
      en: "A storefront connected to orders, stock, fulfilment and customer service — from the first request to the final step.",
      ar: "متجر مرتبط بالطلبات والمخزون والتوصيل وخدمة العملاء، من أول طلب حتى آخر خطوة.",
    },
    challenge: {
      en: "Online sales grow faster than the operations behind them. Orders are copied into spreadsheets, stock is out of sync, customers ask where their order is, and the team handles returns manually.",
      ar: "تنمو المبيعات الإلكترونية أسرع من العمليات خلفها. تُنسخ الطلبات إلى جداول، ويختلف المخزون بين الأنظمة، ويسأل العملاء عن طلباتهم، ويتعامل الفريق مع المرتجعات يدويًا.",
    },
    capabilities: [
      {
        title: { en: "Online storefronts", ar: "المتاجر الإلكترونية" },
        body: {
          en: "Fast, bilingual storefronts with a clear purchase journey.",
          ar: "متاجر سريعة ثنائية اللغة بتجربة شراء واضحة.",
        },
      },
      {
        title: { en: "Orders and stock updates", ar: "تحديث الطلبات والمخزون" },
        body: {
          en: "Orders, customers and stock exchanged between store and back office.",
          ar: "تبادل الطلبات والعملاء والمخزون بين المتجر والنظام الإداري.",
        },
      },
      {
        title: { en: "Order tracking workflow", ar: "مسار تتبع الطلبات" },
        body: {
          en: "Clear statuses and customer notifications from payment to delivery.",
          ar: "حالات واضحة وإشعارات للعميل من الدفع حتى التسليم.",
        },
      },
      {
        title: { en: "Operational alerts", ar: "تنبيهات تشغيلية" },
        body: {
          en: "Low stock, failed payments or delayed shipments flagged to the right person.",
          ar: "تنبيه الشخص المناسب عند انخفاض المخزون أو فشل الدفع أو تأخر الشحن.",
        },
      },
    ],
    services: ["digital-presence", "automation", "erp-crm"],
  },
  {
    slug: "professional-services",
    title: { en: "Professional Services", ar: "الخدمات المهنية" },
    summary: {
      en: "From proposal to project delivery and invoicing, for consultancies and service firms that sell expertise.",
      ar: "من العرض إلى تنفيذ المشروع والفوترة، لشركات الاستشارات والخدمات التي تبيع الخبرة.",
    },
    challenge: {
      en: "Service firms depend on knowing who is working on what, how much of the budget is used and when to invoice. That information usually sits across email, timesheets and accounting.",
      ar: "تعتمد شركات الخدمات على معرفة من يعمل على ماذا، وكم استُهلك من الميزانية، ومتى تصدر الفاتورة. وغالبًا ما تتوزع هذه المعلومات بين البريد وسجلات الوقت والمحاسبة.",
    },
    capabilities: [
      {
        title: { en: "Proposal to project", ar: "من العرض إلى المشروع" },
        body: {
          en: "A signed quotation becomes a project with its tasks and budget.",
          ar: "يتحول عرض السعر الموقّع إلى مشروع بمهامه وميزانيته.",
        },
      },
      {
        title: {
          en: "Timesheets and milestone invoicing",
          ar: "سجلات الوقت والفوترة على مراحل",
        },
        body: {
          en: "Invoice by milestone or hours, from the same project data.",
          ar: "الفوترة حسب المرحلة أو الساعات من بيانات المشروع نفسها.",
        },
      },
      {
        title: { en: "Knowledge assistant", ar: "مساعد معرفي" },
        body: {
          en: "Search past proposals and internal documents with a smart assistant, while keeping private information with the right people.",
          ar: "البحث في العروض السابقة والمستندات الداخلية بمساعد ذكي، مع إتاحة المعلومات للأشخاص المعنيين فقط.",
        },
      },
    ],
    services: ["erp-crm", "custom-software", "ai-solutions"],
  },
]
export const getSolution = (slug: string) =>
  solutions.find((s) => s.slug === slug)

/* ---------------- Work / case studies ---------------- */

export type CaseStudy = {
  slug: string
  /** Public projects only. Anonymized until the client approves disclosure. */
  disclosure: "approved" | "anonymized"
  title: L10n
  industry: L10n
  challenge: L10n
  scope: L10n[]
  solution: L10n
  /** Only verified outcomes. Leave empty rather than estimate. */
  outcomes: L10n[]
  services: string[]
  published: boolean
}

// Projects will be added when ready for publication.
export const caseStudies: CaseStudy[] = []
export const getCase = (slug: string) =>
  caseStudies.find((c) => c.slug === slug && c.published)

/* ---------------- Insights ---------------- */

export const insightCategories: { slug: string; label: L10n }[] = [
  {
    slug: "digital-transformation",
    label: { en: "Digital Transformation", ar: "التحول الرقمي" },
  },
  { slug: "erp-crm", label: { en: "Business & Customers", ar: "إدارة الأعمال والعملاء" } },
  {
    slug: "software-engineering",
    label: { en: "Business Applications", ar: "تطبيقات الأعمال" },
  },
  { slug: "mobile", label: { en: "Mobile Development", ar: "تطوير الجوال" } },
  { slug: "ecommerce", label: { en: "E-Commerce", ar: "التجارة الإلكترونية" } },
  {
    slug: "ai-automation",
    label: {
      en: "Smart Ways to Work",
      ar: "طرق ذكية للعمل",
    },
  },
  {
    slug: "cloud-devops",
    label: { en: "Hosting & Maintenance", ar: "الاستضافة والصيانة" },
  },
  {
    slug: "integration",
    label: { en: "Connecting Your Business", ar: "ربط أعمالك" },
  },
]

export type Article = {
  slug: string
  status: "draft" | "review" | "published"
  category: string
  title: L10n
  excerpt: L10n
  body: L10n[]
  author: string
  publishedAt: string
  seoDescription?: L10n
}

/** No articles are published yet. Add entries with status "published" after editorial review. */
export const articles: Article[] = []
export const publishedArticles = () =>
  articles
    .filter((a) => a.status === "published")
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
